// @ts-nocheck
/**
 * useDialerTelephonyNumbers — Gestion des numeros telephoniques du tenant.
 *
 * 2026-06-04 (Fowler etape 8/x) — Extrait du god-context DialerContext.tsx.
 *
 * Responsabilites :
 *   1. Charger + auto-rafraichir (poll 30s) la liste des numeros Twilio du
 *      tenant via /telephony/numbers (route scopee par tenantNarrowing).
 *   2. Configurer le webhook Twilio entrant (super_admin uniquement, gate
 *      par flag localStorage 'revcall_webhook_configured').
 *   3. Synchroniser activeCallerIds (preference du caller) cote serveur
 *      via PATCH /auth/membership-prefs.
 *   4. Calculer activeCallerNumbers (filter actif + zadarma verified + tri
 *      mobile FR en premier).
 *   5. Gerer selectedCallerId (session-only mono-pick) avec auto-fallback
 *      sur le premier activeCallerNumber valide.
 *
 * Le hook NE TOUCHE PAS au DialerContext : il prend callerData en input
 * et retourne les valeurs + setters.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
// @ts-ignore — api-client en .js
import { api } from '@/api/api-client';
// @ts-ignore — react-query .js
import type { QueryClient } from '@tanstack/react-query';

export interface TelephonyNumber {
  sid: string;
  phoneNumber: string;
  friendlyName?: string;
  areaCode?: string;
  status?: string;
  capabilities: string[];
  provider: string;
  caller_id_verification: string;
  type: 'owned';
}

interface CallerData {
  id?: string;
  is_super_admin?: boolean;
  auth?: {
    activeMembership?: {
      organisationId?: string;
      selectedCallerIds?: string[];
    };
  };
}

interface UseDialerTelephonyNumbersArgs {
  callerData: CallerData | null | undefined;
  queryClient: QueryClient;
}

export interface DialerTelephonyState {
  twilioNumbers: TelephonyNumber[];
  activeCallerIds: string[];
  setActiveCallerIds: (next: string[]) => Promise<void>;
  activeCallerNumbers: TelephonyNumber[];
  selectedCallerId: string | null;
  setSelectedCallerId: (value: string | null) => void;
}

export function useDialerTelephonyNumbers({
  callerData,
  queryClient,
}: UseDialerTelephonyNumbersArgs): DialerTelephonyState {
  const [twilioNumbers, setTwilioNumbers] = useState<TelephonyNumber[]>([]);

  const activeOrgId = callerData?.auth?.activeMembership?.organisationId || null;
  const remoteActiveCallerIds =
    callerData?.auth?.activeMembership?.selectedCallerIds ?? [];

  const [activeCallerIds, _setActiveCallerIdsState] =
    useState<string[]>(remoteActiveCallerIds);

  // Re-sync local state quand la membership reload (org switch, /auth/me refetch).
  useEffect(() => {
    _setActiveCallerIdsState(remoteActiveCallerIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeOrgId, JSON.stringify(remoteActiveCallerIds)]);

  // ── Load tenant numbers (poll 30s) ─────────────────────────────────────────
  useEffect(() => {
    if (!callerData) return;
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.functions.invoke('listTenantNumbers');
        if (cancelled || !data?.numbers) return;
        const normalized: TelephonyNumber[] = data.numbers.map((n: any) => ({
          sid: n.id,
          phoneNumber: n.phone_number,
          friendlyName: n.friendly_name,
          areaCode: n.area_code,
          status: n.status,
          capabilities: Array.isArray(n.capabilities) ? n.capabilities : ['voice'],
          provider: n.provider || 'twilio',
          caller_id_verification: n.caller_id_verification || '',
          type: 'owned',
        }));
        setTwilioNumbers(normalized);
      } catch (_e) {
        /* silent — UI affiche liste vide */
      }
    };
    load();
    const id = setInterval(load, 30_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [callerData?.id]);

  // ── Webhook bootstrap (super_admin only) ──────────────────────────────────
  useEffect(() => {
    if (!callerData?.is_super_admin) return;
    (async () => {
      const flagKey = 'revcall_webhook_configured';
      try {
        if (localStorage.getItem(flagKey) === '1') return;
        const res = await api.functions.invoke('configureIncomingWebhook', {});
        if (res?.status === 200 && (res.data?.success || res.data?.message)) {
          localStorage.setItem(flagKey, '1');
        }
      } catch (err: any) {
        const status = err?.response?.status || err?.status;
        const msg = err?.response?.data || err?.message || '';
        if (
          status === 404 ||
          (typeof msg === 'string' && msg.includes('Deployment does not exist'))
        ) {
          localStorage.setItem(flagKey, '1');
        }
      }
    })();
  }, [callerData?.is_super_admin]);

  // ── setActiveCallerIds (optimistic + rollback) ────────────────────────────
  const setActiveCallerIds = useCallback(
    async (next: string[]) => {
      const dedup = Array.from(
        new Set(
          (next || []).filter((s) => typeof s === 'string' && s.length > 0),
        ),
      );
      _setActiveCallerIdsState(dedup);
      try {
        await api.functions.invoke('updateMembershipPrefs', {
          selectedCallerIds: dedup,
        });
        queryClient.invalidateQueries({ queryKey: ['callerMe'] });
      } catch (err) {
        console.warn('[DialerCtx] updateMembershipPrefs failed; rolling back', err);
        _setActiveCallerIdsState(remoteActiveCallerIds);
      }
    },
    [remoteActiveCallerIds, queryClient],
  );

  // ── activeCallerNumbers (filtre actif + tri par "appel naturel") ──────────
  // Ordre de tri :
  //   1. Mobiles FR Zadarma verified (07/06) — plus "intime" cote prospet,
  //      ET ils sont vraiment routes via le SIP Zadarma (pas de probleme
  //      Twilio "Verified Caller ID" car le routing sortant passe par Zadarma).
  //   2. Mobiles FR Twilio (07/06) — necessite que le numero soit dans la
  //      liste Verified Caller IDs Twilio cote compte (sinon erreur 21210).
  //   3. Fixes (01-05/08-09) — moins naturel pour du cold call.
  const activeCallerNumbers = useMemo<TelephonyNumber[]>(() => {
    const allActive = twilioNumbers.filter((n) => {
      if (n.status !== 'active') return false;
      if (n.provider === 'zadarma' && n.caller_id_verification !== 'verified') {
        return false;
      }
      return true;
    });
    const scoped =
      activeCallerIds.length === 0
        ? allActive
        : allActive.filter((n) => activeCallerIds.includes(n.phoneNumber));
    const isMobileFr = (n: TelephonyNumber) => /^\+33[67]/.test(n.phoneNumber || '');
    const tier = (n: TelephonyNumber): number => {
      const mobile = isMobileFr(n);
      const isZadarmaVerified = n.provider === 'zadarma' && n.caller_id_verification === 'verified';
      if (mobile && isZadarmaVerified) return 0; // top : mobile FR Zadarma verified
      if (mobile) return 1; // mobile FR Twilio
      return 2; // fixe
    };
    return [...scoped].sort((a, b) => tier(a) - tier(b));
  }, [twilioNumbers, activeCallerIds]);

  // ── selectedCallerId (session pick + auto-fallback) ───────────────────────
  const [selectedCallerId, _setSelectedCallerIdRaw] = useState<string | null>(
    null,
  );
  const setSelectedCallerId = useCallback((value: string | null) => {
    _setSelectedCallerIdRaw(value || null);
  }, []);
  useEffect(() => {
    const currentValid =
      selectedCallerId &&
      activeCallerNumbers.some((n) => n.phoneNumber === selectedCallerId);
    if (currentValid) return;
    _setSelectedCallerIdRaw(activeCallerNumbers[0]?.phoneNumber ?? null);
  }, [activeCallerNumbers, selectedCallerId]);

  return {
    twilioNumbers,
    activeCallerIds,
    setActiveCallerIds,
    activeCallerNumbers,
    selectedCallerId,
    setSelectedCallerId,
  };
}
